import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUserDetail() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Edit form
  const [displayName, setDisplayName] = useState('');
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [customPrice, setCustomPrice] = useState('');

  useEffect(() => {
    loadUserDetails();
  }, [userId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadUserDetails = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });

      setUser(response.data.user);
      setRecordings(response.data.recordings);
      setPayments(response.data.payments);
      
      // Set form values
      setDisplayName(response.data.user.display_name || '');
      setSubscriptionTier(response.data.user.subscription_tier);
      setCustomPrice(response.data.user.custom_price || '');
    } catch (error) {
      console.error('Error loading user:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to load user details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        {
          display_name: displayName || null,
          subscription_tier: subscriptionTier,
          custom_price: customPrice ? parseFloat(customPrice) : null
        },
        { headers: getAuthHeaders() }
      );

      toast.success('User updated successfully');
      await loadUserDetails();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });

      toast.success('User deleted successfully');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="text-sm text-gray-600">{user.email}</p>
            </div>
          </div>
          <Button
            variant="destructive"
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete User
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* User Info Card */}
        <Card className="bg-white border-gray-200 shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">User Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Email</label>
              <Input value={user.email} disabled className="bg-gray-50" />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Display Name</label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter display name"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Subscription Tier</label>
              <Select value={subscriptionTier} onValueChange={setSubscriptionTier}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Custom Price (£/month)
                <span className="text-xs text-gray-500 ml-2">(For enterprise/special pricing)</span>
              </label>
              <Input
                type="number"
                step="0.01"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                placeholder="9.99"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">User ID</label>
              <Input value={user.user_id} disabled className="bg-gray-50 text-xs" />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Created At</label>
              <Input
                value={new Date(user.created_at).toLocaleString()}
                disabled
                className="bg-gray-50"
              />
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </Card>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 bg-white border-gray-200 shadow-md">
            <p className="text-sm text-gray-600">Total Recordings</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{recordings.length}</p>
          </Card>

          <Card className="p-6 bg-white border-gray-200 shadow-md">
            <p className="text-sm text-gray-600">Monthly Usage</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">
              {user.subscription_tier === 'free'
                ? `${user.recording_count_current_month || 0}/5`
                : 'Unlimited'}
            </p>
          </Card>

          <Card className="p-6 bg-white border-gray-200 shadow-md">
            <p className="text-sm text-gray-600">Successful Payments</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">
              {payments.filter(p => p.payment_status === 'paid').length}
            </p>
          </Card>
        </div>

        {/* Recordings List */}
        <Card className="bg-white border-gray-200 shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Recordings</h2>
          </div>
          <div className="p-6">
            {recordings.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No recordings yet</p>
            ) : (
              <div className="space-y-3">
                {recordings.map((recording) => (
                  <div key={recording.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{recording.title}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(recording.created_at).toLocaleDateString()} •{' '}
                        {recording.duration ? `${Math.floor(recording.duration / 60)}m ${recording.duration % 60}s` : 'Duration unknown'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Payments List */}
        {payments.length > 0 && (
          <Card className="bg-white border-gray-200 shadow-md">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Payment History</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">
                        ${payment.amount.toFixed(2)} {payment.currency.toUpperCase()}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      payment.payment_status === 'paid'
                        ? 'bg-green-100 text-green-800'
                        : payment.payment_status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {payment.payment_status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
